import React, { useEffect, useMemo,useState } from "react";
import { Dialog, DialogContent, DialogTitle, Box, Typography, TextField, Button, IconButton, Autocomplete } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  createRegionAction,
  clearCreateRegionAction,
  getRegionByIdAction,
  clearGetRegionByIdAction,
  getAllUserAction,
  updateRegionAction,
  clearUpdateRegionAction,
  getAllZonesAction,
  getAllRegionsAction,
} from "../../../store/actions";

const AddRegion = ({ open, onClose, isEdit, regionId, onSuccess }) => {
  const dispatch = useDispatch();
  const [duplicateError, setDuplicateError] = useState("");

  const { successData: usersData, loading: usersLoading } = useSelector((state) => state.getAllUserReducer || {});
  const { successData: zonesData, loading: zonesLoading } = useSelector((state) => state.getAllZonesReducer || {});
  const { successData: regionByIdData, loading: regionByIdLoading, error: regionByIdError } = useSelector((state) => state.getRegionByIdReducer || {});
  const { successData: createSuccess, loading: createLoading, error: createError, errorInfo: createErrorInfo } = useSelector((state) => state.createRegionReducer || {});
  const { successData: updateSuccess, loading: updateLoading, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateRegionReducer || {});
  const { successData: regionsData, loading: regionsLoading } = useSelector(
  (state) => state.getAllRegionsReducer || {}
);
  const users = useMemo(() => {
    if (Array.isArray(usersData)) return usersData;
    if (usersData && usersData.data && Array.isArray(usersData.data.users)) return usersData.data.users;
    return [];
  }, [usersData]);

  const zones = useMemo(() => {
    if (Array.isArray(zonesData)) return zonesData;
    if (zonesData && zonesData.data && Array.isArray(zonesData.data.zones)) return zonesData.data.zones;
    return [];
  }, [zonesData]);

  const regions = useMemo(() => {
  if (Array.isArray(regionsData)) return regionsData;
  if (regionsData && regionsData.data && Array.isArray(regionsData.data.regions)) {
    return regionsData.data.regions;
  }
  return [];
}, [regionsData]);
  const rmUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list.filter((u) => {
      const role = (u.role || u.roleType || u.userRole || u.role_name || "").toString().toUpperCase();
      return role === "RM";
    });
  }, [users]);

  const normalizeRegionName = (name) =>
  (name || "").replace(/\s+/g, "").toLowerCase();

  const regionSchema = Yup.object({
    region_name: Yup.string().trim().required("Region name is required"),
    zone_id: Yup.number().typeError("Zone ID must be a number").required("Zone ID is required"),
    incharge_user_id: Yup.string().trim(),
  });

  const formik = useFormik({
    initialValues: { region_name: "", zone_id: "", incharge_user_id: "" },
    validationSchema: regionSchema,
    enableReinitialize: false,
   onSubmit: (values) => {
  const newNameNorm = normalizeRegionName(values.region_name);

  const duplicate = regions.some((r) => {
    const rName = r?.regionName || r?.region_name || r?.name || "";
    const rZoneId = r?.zoneId || r?.zone_id || r?.zoneID;
    const rId = r?.regionId || r?.id || r?.region_id;

    if (isEdit && String(rId) === String(regionId)) return false;

    return (
      String(rZoneId) === String(values.zone_id) &&
      normalizeRegionName(rName) === newNameNorm
    );
  });

  if (duplicate) {
    setDuplicateError("Region with this name already exists in this zone!");
    return;
  }

  setDuplicateError("");

  const payload = {
    region_name: values.region_name,
    zone_id: Number(values.zone_id),
    incharge_user_id: values.incharge_user_id ? values.incharge_user_id : null,
  };

  if (isEdit && regionId) {
    dispatch(updateRegionAction({ ...payload, endPoint: `region/${regionId}` }));
  } else {
    dispatch(createRegionAction(payload));
  }
},
  });

  useEffect(() => {
    if (open) {
      dispatch(getAllUserAction());
      dispatch(getAllZonesAction());
      dispatch(getAllRegionsAction());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (open && isEdit && regionId) {
      dispatch(getRegionByIdAction({ endPoint: `region/${regionId}` }));
    }
  }, [open, isEdit, regionId, dispatch]);

  useEffect(() => {
    if (open && isEdit && regionByIdData && !regionByIdLoading && !regionByIdError) {
      const r = regionByIdData?.data?.region || regionByIdData?.data || regionByIdData;
      const prefill = {
        region_name: r?.region_name || r?.regionName || r?.name || "",
        zone_id: r?.zone_id ?? r?.zoneId ?? r?.zoneID ?? "",
        incharge_user_id: r?.incharge_user_id || r?.inchargeUserId || r?.userId || "",
      };
      formik.setValues(prefill);
    }
  }, [open, isEdit, regionByIdData, regionByIdLoading, regionByIdError]);

  useEffect(() => {
  if (!formik.values.region_name || !formik.values.zone_id) {
    setDuplicateError("");
    return;
  }

  const newNameNorm = normalizeRegionName(formik.values.region_name);

  const duplicate = regions.some((r) => {
    const rName = r?.regionName || r?.region_name || r?.name || "";
    const rZoneId = r?.zoneId || r?.zone_id || r?.zoneID;
    const rId = r?.regionId || r?.id || r?.region_id;

    if (isEdit && String(rId) === String(regionId)) return false;

    return (
      String(rZoneId) === String(formik.values.zone_id) &&
      normalizeRegionName(rName) === newNameNorm
    );
  });

  setDuplicateError(
    duplicate ? "Region with this name already exists in this zone!" : ""
  );
}, [formik.values.region_name, formik.values.zone_id, regions, isEdit, regionId]);
  useEffect(() => {
    if (!open) return;
    if (createSuccess) {
      toast.success("Region created successfully!");
      formik.resetForm();
      dispatch(clearCreateRegionAction());
      onSuccess?.();
      onClose();
    }
    if (createError) {
      toast.error(createErrorInfo || "Failed to create region");
      dispatch(clearCreateRegionAction());
    }
  }, [open, createSuccess, createError, createErrorInfo, dispatch, formik, onClose, onSuccess]);

  useEffect(() => {
    if (!open) return;
    if (updateSuccess) {
      toast.success("Region updated successfully!");
      formik.resetForm();
      dispatch(clearUpdateRegionAction());
      dispatch(clearGetRegionByIdAction());
      onSuccess?.();
      onClose();
    }
    if (updateError) {
      toast.error(updateErrorInfo || "Failed to update region");
      dispatch(clearUpdateRegionAction());
    }
  }, [open, updateSuccess, updateError, updateErrorInfo, dispatch, formik, onClose, onSuccess]);

  const loading = createLoading || updateLoading || regionByIdLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 4, p: { xs: 2, sm: 4 } } } }}
    >
      <IconButton onClick={onClose} sx={{ position: "absolute", top: 16, right: 16 }}>
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ p: 0, mb: 1 }}>
        <Typography fontSize={28} fontWeight={700}>Region Information</Typography>
        <Typography fontSize={14} color="text.secondary">{isEdit ? "Update Region" : "Create new Region"}</Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mt: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Zone</Typography>
            <Autocomplete
              options={zones.map((z) => ({
                id: z.zoneId || z.id || z.zone_id || z.zoneID,
                name: z.zone_name || z.zoneName || z.name || String(z.zoneId || z.id || z.zone_id || z.zoneID || ""),
              })).filter((o) => o.id)}
              value={(function () {
                const currentId = formik.values.zone_id;
                if (!currentId) return null;
                const found = zones.find((z) => {
                  const zid = z.zoneId || z.id || z.zone_id || z.zoneID;
                  return String(zid) === String(currentId);
                });
                return found
                  ? {
                      id: found.zoneId || found.id || found.zone_id || found.zoneID,
                      name: found.zone_name || found.zoneName || found.name || String(found.zoneId || found.id || found.zone_id || found.zoneID || ""),
                    }
                  : null;
              })()}
              onChange={(_, newValue) => {
                formik.setFieldValue("zone_id", newValue ? Number(newValue.id) : "");
              }}
              loading={zonesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={zonesLoading ? "Loading zones..." : "Select a zone"}
                  fullWidth
                  size="small"
                  error={formik.touched.zone_id && Boolean(formik.errors.zone_id)}
                  helperText={formik.touched.zone_id && formik.errors.zone_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Region Name</Typography>
            <TextField
              name="region_name"
              value={formik.values.region_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              size="small"
              placeholder="Enter Region"
             error={!!duplicateError || (formik.touched.region_name && Boolean(formik.errors.region_name))}
             helperText={duplicateError || (formik.touched.region_name && formik.errors.region_name)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Box>

          <Box mb={4}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Incharge User</Typography>
            <Autocomplete
              options={rmUsers}
              getOptionLabel={(option) => option.name || option.fullName || option.email || ""}
              isOptionEqualToValue={(option, value) => (option.userId || option.id) === (value.userId || value.id)}
              value={rmUsers.find((u) => (u.userId || u.id) === formik.values.incharge_user_id) || null}
              onChange={(_, selected) => {
                formik.setFieldValue("incharge_user_id", selected ? (selected.userId || selected.id || "") : "");
              }}
              onBlur={() => formik.setFieldTouched("incharge_user_id", true)}
              disabled={usersLoading}
              loading={usersLoading}
              noOptionsText={rmUsers.length === 0 ? "No RM users available" : "No match found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={usersLoading ? "Loading users..." : "Search and select incharge"}
                  error={formik.touched.incharge_user_id && Boolean(formik.errors.incharge_user_id)}
                  helperText={formik.touched.incharge_user_id && formik.errors.incharge_user_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection="row">
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{ borderRadius: 3, fontWeight: 600, p: "10px 0px", color: "#000", border: "1px solid #000", textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !!duplicateError}
              sx={{ borderRadius: 3, p: "10px 0px", fontWeight: 600, textTransform: "none", bgcolor: "#5B4CF5", boxShadow: "0px 8px 20px rgba(91,76,245,0.3)", "&:hover": { bgcolor: "#4a3de0" } }}
            >
              {loading ? (isEdit ? "Updating..." : "Submitting...") : isEdit ? "Update Region" : "Submit Data"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

AddRegion.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuccess: PropTypes.func,
};

AddRegion.defaultProps = {
  isEdit: false,
  regionId: "",
  onSuccess: undefined,
};

export default AddRegion;